// Which locations to offer as "Recent" in the destination picker.
//
// The problem this solves, from QA: scanning into a chest of drawers puts every drawer you
// touched into the recents list, so the picker showed four rows reading "Top right drawer",
// "Bottom right drawer", "Bottom left drawer", "Middle left drawer", each under an identical
// dimmed "Storage unit › Bedroom" context line. Five slots spent on one piece of furniture,
// and the answer you actually wanted — the chest — was not among them.
//
// So when several children of the same location are recent, the parent stands in for them.
// The picker renders these rows as ordinary tree rows, so the specific drawer is one tap
// away rather than gone.
//
// Deliberately ONE level of collapsing. Rolling it up repeatedly would turn two recent
// drawers into "Bedroom", then "House", and a recents list that always says "House" is worse
// than the noise it replaced.
//
// Top-level locations are left out of this list entirely, and are never collapsed into.
// The whole point of the section is to skip the walk down the tree, and a row saying "House"
// saves nobody a tap: every top-level location is already a root row in the tree below it.
// Offering both meant the list read "House" and "Basement" as if they were alternatives,
// and the same place appeared twice on one screen.

// Note that `last_used_at` is NOT NULL DEFAULT now() on nodes, so it really means "created or
// used" and every location is a candidate. There is no such thing here as a location that has
// never been touched, which is how a freshly created house ended up billed as a recent
// destination. The null check below is defensive only.
const DEFAULT_LIMIT = 5;
// Two is enough to establish "this is a container I am working inside of". One recent child
// stays as itself, because a single specific place is exactly what the list is for.
const DEFAULT_MIN_SIBLINGS = 2;

// locations: every visible location, each with `id`, `parent_location_id` and `last_used_at`.
// Returns the location records to show, most recently used first.
export function recentDestinations(locations, options = {}) {
  const limit = options.limit ?? DEFAULT_LIMIT;
  const minSiblings = options.minSiblings ?? DEFAULT_MIN_SIBLINGS;
  const list = locations || [];
  const byId = new Map(list.map((l) => [l.id, l]));

  const candidates = list
    .filter((l) => l.last_used_at && l.parent_location_id)
    .sort((a, b) => String(b.last_used_at || "").localeCompare(String(a.last_used_at || "")));

  // How many candidates sit directly under each parent. At or above the threshold, that
  // parent represents all of them.
  const perParent = new Map();
  for (const c of candidates) {
    perParent.set(c.parent_location_id, (perParent.get(c.parent_location_id) || 0) + 1);
  }

  const out = [];
  const seen = new Set();
  for (const candidate of candidates) {
    const parent = byId.get(candidate.parent_location_id);
    // The parent has to be a location we were actually given, and itself nested: a candidate
    // whose parent is outside the visible set or is top-level has nothing to collapse into.
    const collapse = parent
      && parent.parent_location_id
      && (perParent.get(parent.id) || 0) >= minSiblings;
    const row = collapse ? parent : candidate;
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
    if (out.length >= limit) break;
  }
  return out;
}
